import type { PointerEvent } from 'react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import type { Workspace } from '@workspace/domain/workspace/Workspace';
import { Droppable } from '@workspace/view-model-folder/ui/DragAndDrop';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { useCurrentUserId } from '@framework/auth';
import { useActionLogSender } from '@framework/action-log';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faGripVertical, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { MenuButtons } from './MenuButtons';
import { FolderEditModal } from '../FolderEditModal';
import { FolderDeleteModal } from '../FolderDeleteModal';
import { MoveFolderModal } from '../MoveModal/MoveFolderModal';
import { MoveToOtherWorkspaceModal } from '../MoveToOtherWorkspaceModal';
import { CSS } from '@dnd-kit/utilities';
import { useDraggable } from '@dnd-kit/core';

type Props = {
  folder: Folder;
  hasChildren: boolean;
  showEditMenu: boolean;
  workspace: Workspace;
};

type ModalTarget = 'Edit' | 'Delete' | 'Move' | 'MoveToOtherWorkspace';

export const FolderItem: React.FC<Props> = ({ folder, hasChildren, showEditMenu, workspace }: Props) => {
  // @dnd-kit の useDraggable は pointerDown イベントをリスンして、ドラッグ処理を行なっている。
  // 編集メニューに対するクリック時の pointerDown イベントがドラッグとして処理されないように、
  // イベントのバブリングを防止する。
  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => e.stopPropagation();

  const {
    attributes: draggableAttributes,
    listeners: dragListeners,
    setNodeRef,
    transform,
  } = useDraggable({
    id: folder.id,
    attributes: { role: 'div' },
  });
  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const [modalTarget, setModalTarget] = useState<ModalTarget | null>(null);

  const handleEdit = () => setModalTarget('Edit');
  const handleDelete = () => setModalTarget('Delete');
  const handleModalClose = () => setModalTarget(null);
  const handleMoveFolder = () => setModalTarget('Move');
  const handleMoveFolderToOtherWorkspace = () => setModalTarget('MoveToOtherWorkspace');

  const [isDuplicating, setIsDuplicating] = useState(false);
  const currentUserId = useCurrentUserId();
  const actionLogSender = useActionLogSender();

  const handleDuplicateFolder = async () => {
    if (!currentUserId) return;

    try {
      setIsDuplicating(true);
      const onError = (message: string) => toast.error(message);
      toast('フォルダを複製中です...', { id: folder.id, duration: Infinity });
      await ViewModelOperation.duplicateFolder(folder.id, currentUserId, onError, actionLogSender);
      toast.success('フォルダを複製しました | Duplicate Succeeded');
    } catch (e) {
      toast.error('フォルダの複製に失敗しました | Duplicate Failed');
      throw e;
    } finally {
      toast.dismiss(folder.id);
      setIsDuplicating(false);
    }
  };

  return (
    <>
      <Droppable id={folder.id} hoveredClassName="rounded-lg bg-gray-300">
        <div className="px-2" ref={setNodeRef} style={style} {...draggableAttributes}>
          <div className="mb-2 flex h-12 w-full items-center rounded hover:bg-gray-200">
            <div className="flex h-full w-16 cursor-pointer items-center justify-center rounded-l">
              <span className="text-3xl text-brand">
                <FontAwesomeIcon icon={faFolder} />
              </span>
            </div>
            <Link
              className="flex-1 cursor-pointer truncate px-2 text-xl font-bold"
              to={UserPagePathBuilder.workspaceFolderPage(workspace.id, folder.id)}
            >
              {folder.name}
            </Link>

            {isDuplicating && (
              <div>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin text-lg text-gray-700" />
                <span className="pr-2 text-lg text-gray-700">フォルダを複製中です...</span>
              </div>
            )}

            <div className="flex items-center justify-center px-2" onPointerDown={handlePointerDown}>
              {showEditMenu && (
                <>
                  <MenuButtons
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    showDuplicateMenu={true}
                    onDuplicateDirectory={handleDuplicateFolder}
                    onMoveFolder={handleMoveFolder}
                    onMoveToOtherWorkspace={handleMoveFolderToOtherWorkspace}
                  />
                  <button {...dragListeners} className="px-2 hover:bg-gray-400">
                    <FontAwesomeIcon icon={faGripVertical} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </Droppable>

      <FolderEditModal folder={folder} isOpen={modalTarget === 'Edit'} onClose={handleModalClose} />

      <FolderDeleteModal
        folder={folder}
        isOpen={modalTarget === 'Delete'}
        onClose={handleModalClose}
        hasChildren={hasChildren}
      />

      <MoveFolderModal isOpen={modalTarget === 'Move'} onClose={handleModalClose} folder={folder} />

      {currentUserId && (
        <MoveToOtherWorkspaceModal
          isOpen={modalTarget === 'MoveToOtherWorkspace'}
          onClose={handleModalClose}
          folder={folder}
          userId={currentUserId}
          groupId={workspace.groupId}
          workspaceId={workspace.id}
        />
      )}
    </>
  );
};
